import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-h6 font-weight-bold",
  "data-cy": "deposit-title"
}
const _hoisted_2 = {
  key: 0,
  "data-cy": "deposit-context"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = {
  class: "py-2",
  "data-cy": "client-matter-search"
}
const _hoisted_5 = {
  key: 0,
  class: "pt-2",
  "data-cy": "client-level-note"
}
const _hoisted_6 = {
  key: 0,
  class: "flex py-2",
  "data-cy": "trust-account-field"
}
const _hoisted_7 = { class: "w-9/12" }
const _hoisted_8 = {
  key: 2,
  class: "text-yellow-600",
  "data-cy": "first-deposit-warning"
}
const _hoisted_9 = {
  key: 3,
  "data-cy": "deposit-retainer-note"
}
const _hoisted_10 = {
  key: 4,
  class: "text-yellow-600",
  "data-cy": "trust-deposit-qbo-warning"
}
const _hoisted_11 = { class: "py-2" }
const _hoisted_12 = {
  id: "payment-method",
  class: "flex"
}
const _hoisted_13 = { class: "w-9/12" }
const _hoisted_14 = { class: "flex items-center py-2" }
const _hoisted_15 = { class: "w-4/12" }
const _hoisted_16 = { class: "w-3/12" }
const _hoisted_17 = { class: "flex py-2" }
const _hoisted_18 = { class: "w-3/12" }
const _hoisted_19 = {
  for: "deposit-source",
  class: "py-2"
}
const _hoisted_20 = { class: "w-9/12" }
const _hoisted_21 = { class: "my-2 flex" }
const _hoisted_22 = { class: "w-9/12" }
const _hoisted_23 = { class: "my-2 flex" }
const _hoisted_24 = { class: "w-9/12" }

import { computed, onMounted, reactive, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { format } from 'date-fns';
import RouteService from '@/services/RouteService';
import type { UiRouterState } from '@/services/RouteService/UiRouterState';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlResizableTextarea from '@/components/LlResizableTextarea.vue';
import ClientMatterSearch from '@/components/ClientMatterSearch.vue';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import type { AccountDetail } from '@/services/UserService/accountDetail';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  getOrConnectClient,
  getOrConnectMatter,
} from '@/services/accounting/accountingConnectService';
import {
  formatCurrencyInputDisplay,
  formatCurrencyInputTyping,
  parseCurrencyInput,
} from '@/services/utils';
import { reloadParentOnly } from '@/utils/routeHelpers';
import type {
  AccountingMethod,
  LooseRecord as Rec,
  TrustAccountSetting,
} from '@/models/trustDepositTypes';
import { useTrustDepositStore } from '@/stores/accounting/useTrustDepositStore';
import {
  buildDepositMemo,
  formatDepositError,
  isFirstDeposit,
  resolveTrustAccount,
  shouldOfferTrustAccount,
  trustAccountNameTooLong,
} from '@/utils/trustDepositHelpers';


export default /*@__PURE__*/_defineComponent({
  __name: 'TrustDepositModal',
  props: {
    state: {},
    stateParams: {},
    rootScope: {},
    inline: { type: Boolean },
    settlementContext: { type: Boolean }
  },
  emits: ["saved", "cancel"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const meStore = useMeStore();
const clientMatterStore = useClientMatterStore();
const trustDepositStore = useTrustDepositStore();
const { paymentMethods, methodsLoading } = storeToRefs(trustDepositStore);
const { handleError, handleSuccess, handleWarning } = useSnackbarHandler();
const routeService = new RouteService(props);

const me = ref<AccountDetail | null>(null);
const trusts = ref<TrustAccountSetting[]>([]);

const uiState = reactive({
  loading: true,
  saving: false,
  pick: false,
  hasDateError: false,
});

/** Full client/matter records: billing, accounting and trust are read off these. */
const formState = reactive({
  client: null as Rec | null,
  matter: null as Rec | null,
  trust: undefined as TrustAccountSetting | undefined,
  method: null as (AccountingMethod & Rec) | null,
  date: new Date() as Date | null,
  amount: undefined as number | undefined,
  source: '',
  description: '',
});

/**
 * What <ClientMatterSearch> reads and writes: lean option records (id/label only). The watchers
 * below swap the full records into formState whenever the selection changes.
 */
const cm = reactive({ client: null as Rec | null, matter: null as Rec | null });
const cmQuery = ref<Rec | null>(null);
const displayAmount = ref('');
/** The account whose summary page opened the dialog; preselected until a record's own one applies. */
const routeTrustId = ref<string | undefined>();

const isClientLevel = computed(() => !!formState.client?.billing?.billClient);
const firstDeposit = computed(() =>
  isFirstDeposit(formState.client, formState.matter),
);
const nameTooLong = computed(() =>
  trustAccountNameTooLong(formState.client, formState.matter),
);
const showTrustAccount = computed(() =>
  shouldOfferTrustAccount(trusts.value, formState.client, formState.matter),
);
const memo = computed(() =>
  buildDepositMemo(formState.source, formState.description),
);
const firstDepositMessage = computed(() => {
  const subject = formState.matter ? me.value?.terms.matter : 'client';
  const accounts = isClientLevel.value ? 'account' : 'accounts';
  return `This is the first deposit for this ${subject} -- the necessary ${accounts} will be created in QuickBooks`;
});
const isSaveButtonDisabled = computed(
  () =>
    uiState.loading ||
    uiState.saving ||
    uiState.hasDateError ||
    !formState.date,
);

function updateTrustAccount() {
  formState.trust = resolveTrustAccount(
    trusts.value,
    formState.client,
    formState.matter,
    routeTrustId.value,
  );
}

/**
 * Give an unconnected client the firm's billing level, so the right accounts get created. Copies
 * rather than mutates: the record is the one useClientMatterStore caches for every other view.
 */
function setDefaultClientBilling() {
  const client = formState.client as Rec;
  if (client.accounting) return;
  if (typeof client.billing?.billClient === 'undefined') {
    formState.client = {
      ...client,
      billing: {
        ...client.billing,
        billClient: me.value?.company.settings?.clientBilling?.billClient,
      },
    };
  }
  if (formState.client?.billing.billClient) formState.matter = null;
}

async function loadFromRoute() {
  try {
    // useMeStore toasts its own failure and resolves `{}`; stop there rather than toast a TypeError.
    const loaded = await meStore.getMe();
    if (!loaded?.id) return;
    me.value = loaded;
    trusts.value = loaded.company.settings?.trust?.accounts ?? [];
    // All four role flags off is the /options equivalent of the old scope:'all' load. Pairing that
    // with our own account id keeps restricted-access matters we're shared on in the list; a null
    // scopeAccount would silently drop every one of them.
    cmQuery.value = {
      originating: false,
      involved: false,
      reviewer: false,
      primary: false,
      scopeAccount: loaded.id,
      archived: false,
      showSecondarySplitMatter: true,
    };

    const { id, clientId, matterId, client, matter, trustId } =
      routeService.params() ?? {};
    // The `.client.matter.*.deposit` states don't declare `clientId` — the client id arrives as
    // `id`, inherited from the parent `/client/{id}` url. `client` is an object param elsewhere;
    // its id is what /clients/{id} needs.
    const c = clientId || id || client?.id;
    const m = matterId || matter;
    // force: true throughout. The store caches one id at a time, and recording a deposit changes
    // billing.trust server-side — a cached record would re-show the first-deposit warning.
    const [routeMatter, routeClient] = await Promise.all([
      m ? clientMatterStore.getMatterById(m, true) : null,
      c ? clientMatterStore.getClientById(c, true) : null,
    ]);
    if (routeMatter) formState.matter = routeMatter;
    if (routeClient) {
      formState.client = routeClient;
    } else if (formState.matter?.client?.id) {
      // Only the matter is known (e.g. Settlement tab): derive the client, keep the picker usable.
      formState.client = await clientMatterStore.getClientById(
        formState.matter.client.id,
        true,
      );
      uiState.pick = true;
    }
    if (
      !formState.client ||
      (!formState.matter && formState.client.billing?.billClient)
    ) {
      uiState.pick = true;
    }

    routeTrustId.value = trustId;
    updateTrustAccount();

    if (props.settlementContext) {
      uiState.pick = false;
      if (!formState.description) formState.description = 'Settlement Funds';
    }

    // Seeding with the full records is safe: the picker swaps in its own lean ones, and the
    // id-keyed watchers see no change.
    cm.client = formState.client || null;
    cm.matter = formState.matter || null;
  } catch (err) {
    handleError(formatDepositError(err));
  } finally {
    uiState.loading = false;
  }
}

async function syncClient(clientId?: string) {
  if (!clientId) {
    formState.client = null;
    updateTrustAccount();
    return;
  }
  if (formState.client?.id === clientId) return;
  let client: Rec;
  try {
    client = await clientMatterStore.getClientById(clientId, true);
  } catch (err) {
    // A rejection for a client the user has already moved off must not wipe the one they see.
    if (cm.client?.id !== clientId) return;
    // Keeping the previous client would let save() post against one the picker no longer shows.
    formState.client = null;
    cm.client = null;
    handleError(formatDepositError(err));
    return;
  }
  // Nor may a late success: save() would post against a record the picker no longer shows.
  if (cm.client?.id !== clientId) return;
  formState.client = client;
  setDefaultClientBilling();
  if (isClientLevel.value) cm.matter = null;
  updateTrustAccount();
}

async function syncMatter(matterId?: string) {
  // The picker can auto-select a lone matter behind its hidden matter field; a client-level
  // deposit has no matter, so refuse it here.
  if (!matterId || isClientLevel.value) {
    formState.matter = null;
    updateTrustAccount();
    return;
  }
  if (formState.matter?.id === matterId) return;
  let matter: Rec;
  try {
    matter = await clientMatterStore.getMatterById(matterId, true);
  } catch (err) {
    if (cm.matter?.id !== matterId) return;
    formState.matter = null;
    cm.matter = null;
    handleError(formatDepositError(err));
    return;
  }
  if (cm.matter?.id !== matterId) return;
  formState.matter = matter;
  updateTrustAccount();
}

// Keyed on the id, so the picker re-assigning the same selection doesn't refetch.
watch(() => cm.client?.id, syncClient);
watch(() => cm.matter?.id, syncMatter);

// SC-45762: currency-formatted amount; the cursor is restored by hand as commas come and go.
function onAmountInput(e: Event) {
  const el = e.target as HTMLInputElement;
  const { formatted, newCursor } = formatCurrencyInputTyping(
    el.value,
    el.selectionEnd ?? el.value.length,
  );
  el.value = formatted;
  displayAmount.value = formatted;
  el.setSelectionRange(newCursor, newCursor);
  const parsed = parseCurrencyInput(formatted);
  // BUG-DEPOSIT-002: not rounded, as before the migration — fix separately.
  formState.amount = parsed ?? undefined;
}

function onAmountBlur() {
  displayAmount.value = formatCurrencyInputDisplay(formState.amount);
}

function onAmountFocus() {
  // Unformatted while editing, so typing doesn't fight the commas; empty shows the placeholder.
  displayAmount.value =
    formState.amount != null && formState.amount > 0
      ? String(formState.amount)
      : '';
}

function cancel() {
  if (props.inline) emit('cancel');
  else routeService.go('^');
}

/**
 * A parent that refreshes its own figures answers with preventDefault(), and we leave without
 * re-entering its state. Anything that does not answer gets the reload, so a mount with no
 * listener is never left showing a stale balance.
 */
function dismissAfterDeposit() {
  const handled = !!props.rootScope?.$broadcast('trust:depositRecorded')
    .defaultPrevented;
  routeService.go(
    '^',
    {},
    handled
      ? {}
      : {
          reload: reloadParentOnly(routeService.currentStateName()),
        },
  );
}

/** Thrown once connect() has told the user what to do; save() stops without a second toast. */
const CONNECT_REQUIRED = Symbol('connect required');

/**
 * No mount can show the QuickBooks customer picker: the route's `.connect` child has no view to
 * render into, and inline there is no child route at all. So every mount says where to do it.
 */
function connect(accounting: Rec, item: string) {
  if (!accounting.connect) return;
  handleWarning(
    `To record this deposit, the ${item} must first be connected to a QuickBooks customer. Please do this from the ${item} settings.`,
  );
  throw CONNECT_REQUIRED;
}

/**
 * Link the client/matter to a QuickBooks customer first, if it isn't yet. Rejects when the user
 * has to act, which is what stops save().
 */
function ensureConnected(): Promise<void> {
  const client = formState.client as Rec;
  const { matter } = formState;
  const meValue = me.value as AccountDetail;
  if (
    (!matter || matter.accounting || client.billing.billClient) &&
    client.accounting
  ) {
    return Promise.resolve();
  }
  // form records are freshly loaded (force), so they go straight through without a re-fetch.
  if (matter) {
    return getOrConnectMatter(matter, meValue).then((accounting) =>
      connect(accounting, meValue.terms.matter),
    );
  }
  return getOrConnectClient(client, meValue).then((accounting) =>
    connect(accounting, 'client'),
  );
}

function validationProblem(): string | null {
  const { amount, date, client, source, method } = formState;
  if (uiState.hasDateError) return 'Date is invalid';
  if (!client) return 'You have to select a client to record a deposit';
  // Before ensureConnected(), which would otherwise link the client in QuickBooks first.
  if (!formState.matter && !client.billing?.billClient) {
    return `You have to select a ${me.value?.terms.matter} to record a deposit`;
  }
  if (!(amount && amount > 0))
    return 'An amount is required to record a deposit';
  if (!date) return 'Date is required to record a deposit';
  if (!method) return 'Payment Method is required to record a deposit';
  if (!source) return "The 'From' field is required";
  return null;
}

async function save() {
  const problem = validationProblem();
  if (problem) {
    handleWarning(problem);
    return;
  }
  uiState.saving = true;
  try {
    await ensureConnected();
    const client = formState.client as Rec;
    const { matter, method, trust } = formState;
    const { billClient } = client.billing;
    await trustDepositStore.recordDeposit({
      level: billClient ? 'client' : 'matter',
      id: billClient ? client.id : (matter as Rec).id,
      body: {
        amount: formState.amount as number,
        date: format(formState.date as Date, 'yyyy-MM-dd'),
        description: memo.value,
        // BUG-DEPOSIT-001: unguarded, as before the migration — a missing account surfaces as a
        // raw TypeError. Pinned by jest/components/TrustDepositModal.spec.ts; fix separately.
        trust: trust!.id ? { id: trust!.id } : undefined,
        method: { id: method!.id, name: method!.name, type: method!.type },
      },
    });
    handleSuccess('The deposit was recorded in the client trust account');
    if (props.inline) emit('saved');
    else dismissAfterDeposit();
  } catch (err) {
    // A failed save is a warning, as it was before the migration; load failures stay errors.
    if (err !== CONNECT_REQUIRED) handleWarning(formatDepositError(err));
  } finally {
    uiState.saving = false;
  }
}

onMounted(() => {
  loadFromRoute();
  // No toast on failure, as before the migration: the list stays empty (the store keeps the
  // error in methodsError).
  trustDepositStore.loadPaymentMethods().catch(() => {});
});

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    "model-value": !!me.value,
    persistent: "",
    "max-width": "650",
    "data-cy": "trust-deposit-dialog"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "tw text-neutral-700 [&_label]:uppercase [&_label]:text-neutral-500 [&_label]:text-sm" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex align-center justify-space-between pt-5 pl-8 pr-4" }, {
            default: _withCtx(() => [
              (!uiState.loading)
                ? (_openBlock(), _createElementBlock("span", _hoisted_1, " Record Deposit to Trust Account "))
                : _createCommentVNode("", true),
              _createVNode(LlSecondaryButton, {
                class: "close-btn ml-auto",
                icon: "mdi-close",
                variant: "text",
                size: "small",
                "aria-label": "Close",
                "data-cy": "deposit-close",
                onClick: cancel
              })
            ]),
            _: 1
          }),
          (uiState.loading)
            ? (_openBlock(), _createBlock(_component_v_card_text, {
                key: 0,
                class: "px-8 pb-6"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_skeleton_loader, {
                    type: "paragraph, list-item@4, actions",
                    elevation: "0",
                    "data-cy": "deposit-loading"
                  })
                ]),
                _: 1
              }))
            : (_openBlock(), _createBlock(_component_v_card_text, {
                key: 1,
                class: "px-8 pt-2 text-base"
              }, {
                default: _withCtx(() => [
                  (!uiState.pick && !__props.settlementContext)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("h5", null, _toDisplayString(formState.matter?.name), 1),
                        _createElementVNode("h4", null, _toDisplayString(formState.client?.name), 1)
                      ]))
                    : _createCommentVNode("", true),
                  (uiState.pick)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createElementVNode("div", _hoisted_4, [
                          _createVNode(ClientMatterSearch, {
                            cm: cm,
                            "query-filter": cmQuery.value,
                            horizontal: true,
                            "client-only": isClientLevel.value,
                            "client-data-cy": "client-select",
                            "matter-data-cy": "matter-select"
                          }, null, 8, ["cm", "query-filter", "client-only"]),
                          (isClientLevel.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[6] || (_cache[6] = [
                                _createElementVNode("i", null, "Deposit is done at client level", -1)
                              ]))]))
                            : _createCommentVNode("", true)
                        ]),
                        (showTrustAccount.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "w-3/12" }, [
                                _createElementVNode("label", {
                                  for: "deposit-trust-account",
                                  class: "py-2"
                                }, "Trust Account")
                              ], -1)),
                              _createElementVNode("div", _hoisted_7, [
                                _createVNode(LlSelect, {
                                  id: "deposit-trust-account",
                                  modelValue: formState.trust,
                                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formState.trust) = $event)),
                                  items: trusts.value,
                                  "item-title": "label",
                                  "item-value": "id",
                                  placeholder: "Select Trust Account",
                                  variant: "outlined",
                                  density: "compact",
                                  "hide-details": "true",
                                  "return-object": "",
                                  "label-class": "!hidden",
                                  "data-cy": "trust-account-select"
                                }, null, 8, ["modelValue", "items"])
                              ])
                            ]))
                          : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true),
                  (firstDeposit.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                        _createVNode(LlIcon, {
                          size: "small",
                          icon: "mdi-alert"
                        }),
                        _createTextVNode(" " + _toDisplayString(firstDepositMessage.value), 1)
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_9, " This will record a retainer paid by the client into the trust account ")),
                  (firstDeposit.value && nameTooLong.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createVNode(LlIcon, {
                          size: "small",
                          icon: "mdi-alert"
                        }),
                        _cache[8] || (_cache[8] = _createTextVNode(" QuickBooks Online limits trust account names to 100 characters. This account will be created with a truncated name. ", -1))
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _cache[9] || (_cache[9] = _createElementVNode("div", { class: "w-3/12" }, [
                        _createElementVNode("label", {
                          for: "deposit-payment-method",
                          class: "py-2"
                        }, "Payment Method")
                      ], -1)),
                      _createElementVNode("div", _hoisted_13, [
                        _createVNode(LlSelect, {
                          id: "deposit-payment-method",
                          modelValue: formState.method,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.method) = $event)),
                          items: _unref(paymentMethods),
                          "item-title": "name",
                          "item-value": "id",
                          placeholder: "Select Payment Method",
                          variant: "outlined",
                          density: "compact",
                          "hide-details": "true",
                          "return-object": "",
                          loading: _unref(methodsLoading),
                          "label-class": "!hidden",
                          "data-cy": "payment-method-select"
                        }, null, 8, ["modelValue", "items", "loading"])
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_14, [
                    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "w-3/12" }, [
                      _createElementVNode("label", { for: "deposit-date" }, "Date")
                    ], -1)),
                    _createElementVNode("div", _hoisted_15, [
                      _createVNode(LlDatePicker, {
                        id: "deposit-date",
                        modelValue: formState.date,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.date) = $event)),
                        label: "",
                        "label-class": "!hidden",
                        "data-cy": "date-picker",
                        "show-button": false,
                        "hide-details": true,
                        "onValidation:error": _cache[3] || (_cache[3] = ($event: any) => (uiState.hasDateError = $event))
                      }, null, 8, ["modelValue"])
                    ]),
                    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "w-2/12 pr-3 text-right" }, [
                      _createElementVNode("label", { for: "deposit-amount" }, "Amount")
                    ], -1)),
                    _createElementVNode("div", _hoisted_16, [
                      _createVNode(LlTextField, {
                        id: "deposit-amount",
                        "model-value": displayAmount.value,
                        placeholder: "0.00",
                        "data-cy": "deposit-amount",
                        required: "",
                        onInput: onAmountInput,
                        onFocus: onAmountFocus,
                        onBlur: onAmountBlur
                      }, null, 8, ["model-value"])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _createElementVNode("div", _hoisted_18, [
                      _createElementVNode("label", _hoisted_19, [
                        _cache[13] || (_cache[13] = _createTextVNode(" From ", -1)),
                        _createVNode(LlTooltip, { icon: "help" }, {
                          default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                            _createTextVNode(" Trust accounting rules require you to explicitly record the source of funds with all deposits ", -1)
                          ]))]),
                          _: 1
                        })
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_20, [
                      _createVNode(LlTextField, {
                        id: "deposit-source",
                        modelValue: formState.source,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formState.source) = $event)),
                        "data-cy": "deposit-source"
                      }, null, 8, ["modelValue"])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_21, [
                    _cache[14] || (_cache[14] = _createElementVNode("div", { class: "w-3/12" }, [
                      _createElementVNode("label", {
                        for: "deposit-use",
                        class: "py-2"
                      }, "Use")
                    ], -1)),
                    _createElementVNode("div", _hoisted_22, [
                      _createVNode(LlTextField, {
                        id: "deposit-use",
                        modelValue: formState.description,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formState.description) = $event)),
                        "data-cy": "deposit-use"
                      }, null, 8, ["modelValue"])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_23, [
                    _cache[15] || (_cache[15] = _createElementVNode("div", { class: "w-3/12" }, [
                      _createElementVNode("label", {
                        for: "deposit-memo",
                        class: "py-2"
                      }, "Memo")
                    ], -1)),
                    _createElementVNode("div", _hoisted_24, [
                      _createVNode(LlResizableTextarea, {
                        id: "deposit-memo",
                        "model-value": memo.value,
                        rows: 2,
                        disabled: "",
                        "data-cy": "deposit-memo"
                      }, null, 8, ["model-value"])
                    ])
                  ])
                ]),
                _: 1
              })),
          (!uiState.loading)
            ? (_openBlock(), _createBlock(_component_v_card_actions, {
                key: 2,
                class: "px-8 pb-6 pt-2"
              }, {
                default: _withCtx(() => [
                  _createVNode(LlSecondaryButton, {
                    "data-cy": "deposit-cancel",
                    onClick: cancel
                  }, {
                    default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_spacer),
                  _createVNode(LlSecondaryButton, {
                    id: "record-deposit-button",
                    color: "primary",
                    "data-cy": "record-deposit",
                    disabled: isSaveButtonDisabled.value,
                    loading: uiState.saving,
                    onClick: save
                  }, {
                    default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                      _createTextVNode(" Record Deposit ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled", "loading"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})